import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminDashboard';
import { Mail, MailOpen, Trash2, Eye, Search, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { API_URL, formatDate } from '../../lib/utils';
import { authFetch } from '../../lib/auth';
import { toast } from 'sonner';

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await authFetch(`${API_URL}/contact`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await authFetch(`${API_URL}/contact/${id}/read`, {
        method: 'PATCH',
      });
      fetchMessages();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleView = (message) => {
    setSelectedMessage(message);
    if (!message.read) {
      markAsRead(message.id);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) return;

    try {
      const response = await authFetch(`${API_URL}/contact/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Message supprimé');
        fetchMessages();
        if (selectedMessage?.id === id) {
          setSelectedMessage(null);
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredMessages = messages.filter(m => 
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.subject?.toLowerCase().includes(search.toLowerCase())
  );

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <AdminLayout>
      <div data-testid="admin-messages">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#003366]">Messages</h1>
            <p className="text-gray-500 mt-1">
              {messages.length} messages ({unreadCount} non lu{unreadCount > 1 ? 's' : ''})
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="admin-card mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Messages List */}
        <div className="admin-card overflow-hidden">
          <div className="divide-y">
            {filteredMessages.map((message) => (
              <div 
                key={message.id} 
                className={`p-4 hover:bg-gray-50 cursor-pointer flex items-start gap-4 ${!message.read ? 'bg-blue-50/50' : ''}`}
                onClick={() => handleView(message)}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${message.read ? 'bg-gray-100' : 'bg-[#003366]'}`}>
                  {message.read ? (
                    <MailOpen className="text-gray-400" size={20} />
                  ) : (
                    <Mail className="text-white" size={20} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`font-medium ${!message.read ? 'text-[#003366]' : 'text-gray-700'}`}>
                        {message.name}
                      </p>
                      <p className="text-sm text-gray-500">{message.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock size={12} /> {formatDate(message.created_at)}
                      </span>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDelete(message.id); }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  <p className={`font-medium mt-1 ${!message.read ? 'text-[#003366]' : 'text-gray-700'}`}>
                    {message.subject}
                  </p>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">{message.message}</p>
                </div>
              </div>
            ))}
            {filteredMessages.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                Aucun message trouvé
              </div>
            )}
          </div>
        </div>

        {/* Message Detail Dialog */}
        <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#003366]">
                {selectedMessage?.subject}
              </DialogTitle>
            </DialogHeader>
            
            {selectedMessage && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className="w-12 h-12 bg-[#003366] rounded-full flex items-center justify-center text-white font-bold">
                    {selectedMessage.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-[#003366]">{selectedMessage.name}</p>
                    <p className="text-sm text-gray-500">{selectedMessage.email}</p>
                    {selectedMessage.phone && (
                      <p className="text-sm text-gray-500">{selectedMessage.phone}</p>
                    )}
                  </div>
                </div>

                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Clock size={14} />
                  {formatDate(selectedMessage.created_at)}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>

                <div className="flex gap-4 pt-4 border-t">
                  <a 
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="flex-1"
                  >
                    <Button className="w-full bg-[#003366] hover:bg-[#002244]">
                      <Mail size={16} className="mr-2" /> Répondre
                    </Button>
                  </a>
                  <Button 
                    variant="destructive"
                    onClick={() => handleDelete(selectedMessage.id)}
                  >
                    <Trash2 size={16} className="mr-2" /> Supprimer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
